/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { toTitleCase } from '../../../../../app/shared/functions';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonAction, ConfirmDeleteDialogData, ConfirmDialogService } from '@epg/ng-components';
import {
  faCalendar,
  faExclamationCircle,
  faExclamationTriangle,
  faRepeat,
  faSpinnerThird
} from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { OneAppButtonConfig } from '../../../../../app/shared/oneapp-header/oneapp-header.component';
import { TaskAssignmentType } from '../../../../../enums/task-assignment-type.enum';
import { RecurringModel, ScheduledTaskModel } from '../../../../../models/scheduled-task.model';
import {
  CustomDateFormatService,
  DateFormat
} from '../../../../../services/custom-date-format/custom-date-format.service';
import { EmployeeService } from '../../../../../services/employee.service';
import {
  RecurringConfig,
  ScheduledTaskService
} from '../../../../../services/scheduled-task.service';
import { WorkflowService } from '../../../../../services/workflow.service';

const DELETE_CALL_BACK_IDENTIFIER = 'delete';
@Component({
  selector: 'app-scheduled-task-detail',
  templateUrl: './scheduled-task-detail.component.html',
  styleUrls: ['./scheduled-task-detail.component.scss'],
  standalone: false
})
export class ScheduledTaskDetailComponent implements OnInit, OnDestroy {
  faSpinnerThird = faSpinnerThird;
  faCalendar = faCalendar;
  faRepeat = faRepeat;
  DateFormat = DateFormat;

  faExclamationCircle = faExclamationCircle;
  faExclamationTriangle = faExclamationTriangle;
  scheduledTaskSubscription?: Subscription;

  TaskAssignmentType = TaskAssignmentType;
  loadingTask: boolean = false;
  scheduledTask$ = this.route.params.pipe(
    switchMap((params) => {
      this.scheduledTaskSubscription?.unsubscribe();
      this.loadingTask = true;
      let scheduledTask$ = this.scheduledTaskService.getScheduledTask(params.scheduledTaskId);
      this.scheduledTaskSubscription = scheduledTask$.subscribe(() => {
        this.loadingTask = false;
      });
      return scheduledTask$;
    })
  );

  buttonAction = ButtonAction;
  deleteButtonConfig: OneAppButtonConfig = {
    id: 'delete_task',
    callBackIdentifier: DELETE_CALL_BACK_IDENTIFIER,
    disabled: this.loadingTask,
    requiredPermissions: []
  };
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private scheduledTaskService: ScheduledTaskService,
    private employeeService: EmployeeService,
    private workflowService: WorkflowService,
    public customDateFormatService: CustomDateFormatService,
    private readonly dialogService: ConfirmDialogService
  ) {}
  ngOnInit(): void {
    this.employeeService.getEmployees();
    this.employeeService.getEmployeeGroups();

    this.scheduledTaskService.scheduledTaskSelectionChanged.subscribe((changedScheduledTask) => {
      if (!changedScheduledTask) {
        this.router.navigate(['../'], { relativeTo: this.route });
      }
    });
  }
  ngOnDestroy() {
    this.scheduledTaskSubscription?.unsubscribe();
  }

  getEmployeeName(id: string): string | undefined {
    return this.employeeService.getEmployeeUserNameFromID(id) ?? undefined;
  }

  getEmployeeGroupName(id: string): string | undefined {
    var grpName = this.employeeService.getEmployeeGroupNameFromID(id);
    return grpName ?? undefined;
  }

  async navigateToWorkflow(name: string): Promise<void> {
    if (this.workflowService.workflows.length === 0) {
      await this.workflowService.searchWorkflow({
        search_criteria: { is_deleted: { $not: { $eq: true } } },
        limit: 0,
        skip: 0,
        sort: '-updatedAt',
        select: '_id name'
      });
    }
    const id = this.workflowService.getWorkflowIdFromName(name);
    if (id) this.router.navigateByUrl(this.getWorkflowRoute(id));
  }

  getWorkflowRoute(id: string): string {
    const combinedId = this.workflowService.getCombinedIdFromWorkflowId(id);
    const targetId = combinedId ?? id;

    return `home/workflow/${targetId}`;
  }

  deleteScheduledTask(scheduledTask: ScheduledTaskModel) {
    const parsedTaskScheduledAtDate = this.customDateFormatService.formatDate(
      scheduledTask.scheduledAt
    );
    const title = $localize`:delete scheduled task header@@deleteScheduledTask:Delete Scheduled Task`;
    const msg = $localize`:delete scheduled task confirmation text Task@@deleteScheduledTaskConfirmationText:Do you really want to delete this task scheduled for ${parsedTaskScheduledAtDate}?`;
    const confirmButtonText = $localize`:button text delete@@buttonDelete:DELETE`;
    const cancelButtonText = $localize`:button text cancel@@buttonCancel:CANCEL`;
    const dialogData: ConfirmDeleteDialogData = {
      itemName: scheduledTask.taskName,
      title: title,
      message: msg,
      confirmText: toTitleCase(confirmButtonText),
      cancelText: toTitleCase(cancelButtonText)
    };
    this.dialogService.openConfirmDelete(dialogData).subscribe((result: boolean) => {
      if (result == true) {
        this.scheduledTaskService.deleteScheduleTaskEvent.next(scheduledTask);
      }
    });
  }

  getAssigneeLabel(
    assignedTo: string[],
    assignedToEmployeeOrGroup: TaskAssignmentType
  ): string | undefined {
    let label: string | undefined = undefined;

    if (assignedToEmployeeOrGroup === TaskAssignmentType.EMPLOYEE) {
      label = this.getEmployeeName(assignedTo[0]);
    } else if (assignedToEmployeeOrGroup === TaskAssignmentType.EMPLOYEEGROUP) {
      let groups = assignedTo
        .map((groupId) => this.getEmployeeGroupName(groupId))
        .filter((groupName) => groupName != undefined);
      if (groups.length > 0) {
        label = groups.join(', ');
      }
    }

    return label;
  }

  checkMissingAssignments(
    assignedTo: string[],
    assignedToEmployeeOrGroup: TaskAssignmentType
  ): number {
    let missingCount = 0;

    if (assignedToEmployeeOrGroup === TaskAssignmentType.EMPLOYEE) {
      if (!this.getEmployeeName(assignedTo[0])) {
        missingCount = 1;
      }
    } else if (assignedToEmployeeOrGroup === TaskAssignmentType.EMPLOYEEGROUP) {
      assignedTo.forEach((assignee) => {
        if (!this.getEmployeeGroupName(assignee)) {
          missingCount++;
        }
      });
    }

    return missingCount;
  }

  getRecurringConfigFromService(
    scheduledTaskRecurring: RecurringModel,
    scheduledAt: string
  ): string {
    return this.scheduledTaskService.getRecurringConfig(
      scheduledTaskRecurring,
      RecurringConfig.standalone,
      scheduledAt
    );
  }
}
